import { BASEURL } from '../utils/constants';
import * as Sentry from '@sentry/react';
import axios from 'axios';

/**
 * Creates an axios instance with common request logic
 * @param getAuthHeaders - Function that returns authentication headers (if needed)
 * @param errorPrefix - Prefix for error messages (e.g., 'public request' or 'request')
 */
export const createAxiosInstance = (getAuthHeaders: () => Promise<Record<string, string>>, errorPrefix = 'request') => {
  const request = async ({
    method,
    path,
    body,
    headers = {},
    responseType,
  }: {
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    path: string;
    body?: Record<string, unknown> | FormData | unknown[] |null;
    headers?: Record<string, string>;
    responseType?: 'blob' | 'arraybuffer' | 'json' | 'text';
  }) => {
    const authHeaders = await getAuthHeaders();

    const config: Record<string, unknown> = {
      headers: {
        ...authHeaders,
        Accept: 'application/json',
        // Don't set Content-Type for FormData - let axios handle it automatically
        ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...headers,
      },
    };
    if (body) {
      config.data = body;
    }
    if (responseType) {
      config.responseType = responseType;
    }

    try {
      return await axios({ method, url: BASEURL + path, ...config });
    } catch (error) {
      console.error(`Error in ${errorPrefix}: ${error}`);

      if (axios.isAxiosError(error) && error.response?.status !== 404) {
        Sentry.captureException(error, {
          tags: { http_status: error.response?.status.toString() },
        });
      }
      throw error;
    }
  };

  const get = async (path: string) => {
    return await request({ method: 'GET', path });
  };

  const post = async (path: string, body: Record<string, unknown> | FormData | unknown[]) => {
    return await request({ method: 'POST', path, body });
  };

  const put = async (path: string, body: Record<string, unknown> | FormData) => {
    return await request({ method: 'PUT', path, body });
  };

  const patch = async (path: string, body: Record<string, unknown> | FormData) => {
    return await request({ method: 'PATCH', path, body });
  };

  const del = async (path: string) => {
    return await request({ method: 'DELETE', path });
  };

  return { get, post, patch, put, del, axiosRequest: request };
};

